import { web3 } from '../web3';

/**
 * CURRENCY CONSISTENCY UTILITIES
 *
 * This module provides utilities for handling currency conversions and ensuring
 * financial calculations use consistent currency units. It addresses issues where
 * different parts of the system might use different currency representations
 * (e.g., USD vs USDT, or different decimal places).
 *
 * IMPORTANT ASSUMPTIONS:
 * - All stablecoin amounts (USDT, USDC, DAI) are treated as equivalent to USD for calculation purposes
 * - All amounts are expected to be in the base unit (e.g., 1 USDT = 1.00, not 1000000 for micro-units)
 * - Blockchain operations use Wei for ETH and equivalent precision for stablecoins
 * - Exchange rates are assumed to be 1:1 for stablecoins unless explicitly handled
 */

export interface CurrencyConfig {
  /** Currency symbol (e.g., 'USDT', 'ETH') */
  symbol: string;
  /** Number of decimal places for this currency */
  decimals: number;
  /** Whether this currency is pegged to USD (stablecoin) */
  isStablecoin: boolean;
  /** Blockchain address for token contracts */
  contractAddress?: string;
}

export const SUPPORTED_CURRENCIES: Record<string, CurrencyConfig> = {
  USDT: {
    symbol: 'USDT',
    decimals: 6, // USDT uses 6 decimals
    isStablecoin: true,
    contractAddress: '0x341218Eb704b45e72FFB73724E33a91F66f799dF', // BSC USDT
  },
  USDC: {
    symbol: 'USDC',
    decimals: 6, // USDC uses 6 decimals
    isStablecoin: true,
    contractAddress: '0x64544969ed7EBf5f083679233325356EbE738930', // BSC USDC
  },
  DAI: {
    symbol: 'DAI',
    decimals: 18, // DAI uses 18 decimals like ETH
    isStablecoin: true,
    contractAddress: '0xF6914d4a2107277cdFF7Be6EBb98C9AE8E8D9eA5', // BSC DAI
  },
  ETH: {
    symbol: 'ETH',
    decimals: 18, // ETH uses 18 decimals
    isStablecoin: false,
    contractAddress: '0x2170Ed0880ac9A755fd29B2688956BD959F933F8', // BSC ETH
  },
};

/**
 * Converts a currency amount to its blockchain representation (Wei equivalent)
 * @param amount - Amount in human-readable format (e.g., 1.5 for 1.5 USDT)
 * @param currencySymbol - Currency symbol (e.g., 'USDT', 'ETH')
 * @returns Amount in smallest unit (Wei for ETH, equivalent for stablecoins)
 */
export function toBlockchainUnits(amount: number, currencySymbol: string): string {
  if (!amount || amount < 0) {
    throw new Error(`Invalid amount: ${amount}`);
  }

  const config = SUPPORTED_CURRENCIES[currencySymbol];
  if (!config) {
    throw new Error(`Unsupported currency: ${currencySymbol}`);
  }

  // For stablecoins, we treat the input as dollars (e.g., 1.5 = 1.5 USDT)
  // For ETH, we treat the input as ether
  return web3.utils.toWei(amount.toString(), 'ether');
}

/**
 * Converts from blockchain units back to human-readable format
 * @param blockchainAmount - Amount in blockchain units (Wei)
 * @param currencySymbol - Currency symbol
 * @returns Amount in human-readable format
 */
export function fromBlockchainUnits(blockchainAmount: string, currencySymbol: string): number {
  const config = SUPPORTED_CURRENCIES[currencySymbol];
  if (!config) {
    throw new Error(`Unsupported currency: ${currencySymbol}`);
  }

  const etherAmount = web3.utils.fromWei(blockchainAmount, 'ether');
  return parseFloat(etherAmount);
}

/**
 * Calculates token issuance based on investment amount and token price
 * Ensures both amounts are in the same currency before calculation
 *
 * @param totalInvestedAmount - Total amount invested (in currency units, e.g., USDT)
 * @param tokenPrice - Price per token (in same currency as totalInvestedAmount)
 * @param currencySymbol - Currency symbol for validation
 * @returns Number of tokens that can be issued (integer, floor-rounded)
 */
export function calculateTokenIssuance(
  totalInvestedAmount: number,
  tokenPrice: number,
  currencySymbol: string
): number {
  // Validate inputs
  if (!totalInvestedAmount || totalInvestedAmount < 0) {
    throw new Error(`Invalid total invested amount: ${totalInvestedAmount}`);
  }

  if (!tokenPrice || tokenPrice <= 0) {
    throw new Error(`Invalid token price: ${tokenPrice}`);
  }

  // Validate currency
  const config = SUPPORTED_CURRENCIES[currencySymbol];
  if (!config) {
    throw new Error(`Unsupported currency for token calculation: ${currencySymbol}`);
  }

  // Both amounts should be in the same currency units
  // e.g., if totalInvestedAmount is 1500, tokenPrice should be 1.5 (both in USDT)
  const tokensIssued = Math.floor(totalInvestedAmount / tokenPrice);

  if (tokensIssued < 0) {
    throw new Error('Token calculation resulted in negative value');
  }

  return tokensIssued;
}

/**
 * Validates that two currency amounts are compatible for mathematical operations
 * @param amount1 - First amount
 * @param currency1 - Currency of first amount
 * @param amount2 - Second amount
 * @param currency2 - Currency of second amount
 * @throws Error if currencies are incompatible
 */
export function validateCurrencyCompatibility(
  amount1: number,
  currency1: string,
  amount2: number,
  currency2: string
): void {
  const config1 = SUPPORTED_CURRENCIES[currency1];
  const config2 = SUPPORTED_CURRENCIES[currency2];

  if (!config1 || !config2) {
    throw new Error(`Unsupported currency: ${!config1 ? currency1 : currency2}`);
  }

  // For now, we only allow operations between the same currency
  // In the future, this could be extended to handle exchange rates
  if (currency1 !== currency2) {
    throw new Error(
      `Currency mismatch: cannot perform operation between ${currency1} and ${currency2}. ` +
        'Both amounts must be in the same currency.'
    );
  }

  // Additional validation could include checking decimal places, etc.
}

/**
 * Formats a currency amount for display
 * @param amount - Amount in human-readable format
 * @param currencySymbol - Currency symbol
 * @returns Formatted string (e.g., "1,500.00 USDT")
 */
export function formatCurrency(amount: number, currencySymbol: string): string {
  const config = SUPPORTED_CURRENCIES[currencySymbol];
  if (!config) {
    return `${amount.toFixed(2)} ${currencySymbol}`;
  }

  return (
    new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: config.decimals,
    }).format(amount) + ` ${currencySymbol}`
  );
}

/**
 * Gets the contract address for a currency token
 * @param currencySymbol - Currency symbol
 * @returns Contract address or undefined if not applicable
 */
export function getCurrencyContractAddress(currencySymbol: string): string | undefined {
  const config = SUPPORTED_CURRENCIES[currencySymbol];
  return config?.contractAddress;
}

/**
 * Checks if a currency is a stablecoin
 * @param currencySymbol - Currency symbol
 * @returns True if stablecoin, false otherwise
 */
export function isStablecoin(currencySymbol: string): boolean {
  const config = SUPPORTED_CURRENCIES[currencySymbol];
  return config?.isStablecoin || false;
}
